import { cn } from "@/utils/cn";

interface SkeletonLoaderProps {
    className?: string;
    variant?: 'text' | 'rectangle' | 'circle' | 'table';
    rows?: number;
    columns?: number;
    showPagination?: boolean;
}

export const SkeletonLoader = ({ 
    className, 
    variant = 'rectangle',
    rows = 1,
    columns = 1,
    showPagination = false
}: SkeletonLoaderProps) => {
    if (variant === 'table') {
        return (
            <div className={cn("flex flex-col", className)}>
                {/* Table Header */}
                <div className={cn("grid gap-4 pb-4 border-b border-stroke", `grid-cols-${columns}`)}>
                    {[...Array(columns)].map((_, i) => (
                        <div key={i} className="h-4 bg-gray-200 rounded animate-pulse" />
                    ))}
                </div>

                {/* Table Rows */}
                {[...Array(rows)].map((_, rowIndex) => (
                    <div 
                        key={rowIndex} 
                        className={cn("grid gap-4 py-4 border-b border-stroke", `grid-cols-${columns}`)}
                    >
                        {[...Array(columns)].map((_, colIndex) => (
                            <div 
                                key={colIndex} 
                                className="h-4 bg-gray-200 rounded animate-pulse"
                                style={{ width: colIndex === 1 ? '80%' : '90%' }}
                            />
                        ))}
                    </div>
                ))}

                {/* Pagination */}
                {showPagination && (
                    <div className="flex justify-between items-center mt-6">
                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        <div className="flex gap-2">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (variant === 'circle') {
        return (
            <div className={cn("rounded-full bg-gray-200 animate-pulse", className)} />
        );
    }

    if (variant === 'text') {
        return (
            <div className="flex flex-col gap-2">
                {[...Array(rows)].map((_, i) => (
                    <div 
                        key={i} 
                        className={cn("h-4 bg-gray-200 rounded animate-pulse", className)}
                    />
                ))}
            </div>
        );
    }

    // Default: rectangle
    return (
        <div className={cn("bg-gray-200 rounded animate-pulse", className)} />
    );
};

interface TableSkeletonProps {
    rows?: number;
    columns?: number;
    showPagination?: boolean;
    showCardTitle?: boolean;
    className?: string;
}

export const TableSkeleton = ({ 
    rows = 8, 
    columns = 6, 
    showPagination = true,
    showCardTitle = true,
    className
}: TableSkeletonProps) => {
    return (
        <div className={cn("flex flex-col bg-white rounded-2xl p-6 border border-stroke", className)}>
            {/* Card Title */}
            {showCardTitle && (
                <SkeletonLoader variant="rectangle" className="h-7 w-64 mb-8" />
            )}
            
            {/* Table */}
            <SkeletonLoader 
                variant="table" 
                rows={rows} 
                columns={columns}
                showPagination={showPagination}
            />
        </div>
    );
};

interface PageSkeletonProps {
    showHeader?: boolean;
    headerTitle?: string;
    showTable?: boolean;
    tableRows?: number;
    tableColumns?: number;
    showPagination?: boolean;
    className?: string;
}

export const PageSkeleton = ({
    showHeader = true,
    showTable = true,
    tableRows = 8,
    tableColumns = 6,
    showPagination = true,
    className
}: PageSkeletonProps) => {
    return (
        <div className={cn("flex flex-col gap-y-6 max-w-[1240px] mx-auto mt-6", className)}>
            {/* Header */}
            {showHeader && (
                <div className="flex flex-row justify-between">
                    <div className="flex flex-col gap-y-2">
                        <SkeletonLoader variant="rectangle" className="h-8 w-48" />
                    </div>
                </div>
            )}

            {/* Table Card */}
            {showTable && (
                <TableSkeleton 
                    rows={tableRows}
                    columns={tableColumns}
                    showPagination={showPagination}
                />
            )}
        </div>
    );
};
